/**
 * Web Push Notifications helper (VAPID).
 *
 * Fluxo:
 *  1. `isPushSupported()` → verifica se navegador suporta.
 *  2. `getPushStatus()` → devolve { supported, permission, subscribed }.
 *  3. `enablePush()` → pede permissão, cria subscription no navegador com a
 *     VAPID public key vinda do backend e envia para /api/push/subscribe.
 *  4. `disablePush()` → cancela subscription local e informa o backend.
 */
import { api } from "./api";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function isPushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export async function getPushStatus() {
  if (!isPushSupported()) return { supported: false, permission: "denied", subscribed: false };
  const permission = Notification.permission;
  let subscribed = false;
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    subscribed = !!sub;
  } catch (err) { console.warn("getPushStatus:", err); }
  return { supported: true, permission, subscribed };
}

async function fetchPublicKey() {
  const { data } = await api.get("/push/public-key");
  return data?.public_key || "";
}

export async function enablePush() {
  if (!isPushSupported()) throw new Error("Navegador não suporta notificações push.");
  const perm = await Notification.requestPermission();
  if (perm !== "granted") throw new Error("Permissão para notificações foi negada.");

  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    const publicKey = await fetchPublicKey();
    if (!publicKey) throw new Error("VAPID public key não configurada no servidor.");
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }
  const json = sub.toJSON();
  await api.post("/push/subscribe", { endpoint: json.endpoint, keys: json.keys });
  return true;
}

export async function disablePush() {
  if (!isPushSupported()) return true;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return true;
  const endpoint = sub.endpoint;
  try { await sub.unsubscribe(); } catch (err) { console.warn("disablePush unsubscribe:", err); }
  try { await api.post("/push/unsubscribe", { endpoint }); } catch (err) { console.warn("disablePush backend:", err); }
  return true;
}

export async function sendTestPush() {
  const { data } = await api.post("/push/test", {
    title: "Notificação de teste",
    body: "Se você recebeu isto, o Push está funcionando!",
    url: "/",
  });
  return data;
}
